import '../../../styles/PageCommon.css';
import './BranchDistributionPage.css';
import { ReactComponent as ChevronLeft } from '../../../assets/icons/chevron-left-icon.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/download-icon.svg';
import { ReactComponent as LoadingIcon } from '../../../assets/icons/upload-loading-icon.svg';
import EditableCell from '../../../components/common/EditableCell';
import InfoTooltip from '../../../components/common/InfoTooltip/InfoTooltip';
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from 'react';
import { distributionService } from '../../../services/distributionService';
import { parseApiError } from '../../../utils/parseErrorMessage';
import LoadingAnimation from '../../../components/common/LoadingAnimation/LoadingAnimation';
import { NotificationContainer } from '../../../components/common/NotificationContainer';
import NotificationModal from '../../../components/common/NotificationModal';
import Pagination from '../../../components/common/Pagination/Pagination';
import { useFilterParams } from '../../../hooks/useFilterParams';
import { formatToAbbrevMonthYear, numberToKiloFormat } from '../../../utils/utils';
import ColumnFilterDropdown from '../../../components/common/ColumnFilterDropdown';

const COLUMNS = [
    { key: 'sku_code', label: 'СКЮ код' },
    { key: 'sku_name', label: 'Наименование товара' },
    { key: 'total_available_quantity_in_mc', label: 'Наличие товара на хабе' },
    { key: 'available_quantity_in_mc', label: 'Наличие товара' },
    { key: 'average_l3m_quantity_in_mc', label: 'Средние продажи за последние 3 мес' },
    { key: 'average_f3m_quantity_in_mc', label: 'Средние продажи за будущие 3 мес' },
    { key: 'recommended_quantity_in_mc', label: 'Рекомендуемое кол-во' },
    { key: 'adjusted_quantity_in_mc', label: 'Распределить в кол-ве' },
    // { key: 'readiness_for_target_per_sku', label: 'Готовность к плану' },
];

const DEFAULT_FILTERS = {
    hub_name: null,
    branch_name: null,
    page: 1,
    page_size: 10,
    ...Object.fromEntries(COLUMNS.map(({ key }) => [key, []])),
};

export default function BranchDistributionPage() {
    const navigate = useNavigate();
    const [openEditDropdown, setOpenEditDropdown] = useState(null);

    const [planningDate, setPlanningDate] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [distributionBranchTotalItems, setDistributionBranchTotalItems] = useState(0);
    const [distributionBranchData, setDistributionBranchData] = useState([]);
    const [distributionError, setDistributionError] = useState("");
    const [isUpdated, setIsUpdated] = useState(false);
    const [branchDistributionSummary, setBranchDistributionSummary] = useState(null);
    const [branchSummaryInfo, setBranchSummaryInfo] = useState(null);
    const [readinessInfo, setReadinessInfo] = useState(null);

    const [editingDistributionBranchRows, setEditingDistributionBranchRows] = useState({});
    const [filterOptions, setFilterOptions] = useState({});
    const [refreshKey, setRefreshKey] = useState(0);
    const distributionBranchCellKey = (row, index) => `${row.sku_code}_${index}`;

    useEffect(() => {
        const handleClickOutside = () => setOpenEditDropdown(null);
        if (openEditDropdown) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [openEditDropdown]);

    // --- Filters State and Logic ---
    const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);

    const colOptions = useMemo(() => {
        const result = {};
        Object.entries(filterOptions).forEach(([key, values]) => {
            result[key] = (values || []).map(String);
        });
        return result;
    }, [filterOptions]);

    const handleColFilterApply = (key, vals) => updateFilters({ [key]: vals, page: 1 });
    const getColSelected = (key) => filters[key] || [];

    // Branch details + summary — re-fetches on filter change or after a save (refreshKey).
    useEffect(() => {
        if (!filters.branch_name) return;
        let cancelled = false;
        setIsLoadingData(true);
        const columnFilters = Object.fromEntries(
            COLUMNS.map(({ key }) => [key, filters[key]]).filter(([, v]) => v?.length)
        );
        Promise.allSettled([
            distributionService.getBranchDetails(filters.branch_name, filters.page, filters.page_size, columnFilters),
            distributionService.getBranchDistributionSummary(filters.branch_name),
            distributionService.getBranchSummaryInformationIcon(filters.branch_name),
            distributionService.getReadinessInfoIcon(),
        ]).then(([detailsRes, summaryRes, infoRes, readinessInfo]) => {
            if (cancelled) return;
            if (detailsRes.status === 'fulfilled' && detailsRes.value) {
                setDistributionBranchTotalItems(detailsRes.value.total_items);
                if (detailsRes.value.items) setDistributionBranchData(detailsRes.value.items);
                if (detailsRes.value.filter_options) setFilterOptions(detailsRes.value.filter_options);
                if (detailsRes.value.planning_date) setPlanningDate(detailsRes.value.planning_date);
            } else if (detailsRes.status === 'rejected') {
                setDistributionError(parseApiError(detailsRes.reason) || "");
            }
            if (summaryRes.status === 'fulfilled' && summaryRes.value) setBranchDistributionSummary(summaryRes.value);
            else if (summaryRes.status === 'rejected') setDistributionError(parseApiError(summaryRes.reason) || "");
            if (infoRes.status === 'fulfilled' && infoRes.value) setBranchSummaryInfo(infoRes.value);
            if (readinessInfo.status === 'fulfilled' && readinessInfo.value) setReadinessInfo(readinessInfo.value);
            
        }).finally(() => { if (!cancelled) setIsLoadingData(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filters.branch_name, filters.page, filters.page_size,
        filters.sku_code, filters.sku_name,
        filters.total_available_quantity_in_mc, filters.available_quantity_in_mc,
        filters.average_l3m_quantity_in_mc, filters.average_f3m_quantity_in_mc,
        filters.recommended_quantity_in_mc, filters.adjusted_quantity_in_mc,
        filters.readiness_for_target_per_sku,
        refreshKey,
    ]);

    const handleUpdateAdjustedQty = (skuCode, adjustedQty) => {
        distributionService.updateBranchAdjustedQty(filters.branch_name, skuCode, adjustedQty)
            .then(response => {
                if (response.rows_updated) {
                    setIsUpdated(true);
                }
            })
            .catch(error => {
                const errorMessage = parseApiError(error)
                setDistributionError(errorMessage ? errorMessage : "");
            })
            .finally(() => { setRefreshKey(k => k + 1); });
    };

    const handleDownloadExcel = async () => {
        setDownloading(true);
        try {
            const response = await distributionService.branchDownloadExcel(filters.branch_name);

            // Create blob URL
            const blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });

            const url = window.URL.createObjectURL(blob);

            // Extract filename from header (if exists)
            const contentDisposition = response.headers["content-disposition"];
            let filename = "download.xlsx";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match?.[1]) filename = match[1];
            }

            // Create temp link
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;

            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        }
        setDownloading(false);
    }

    return (
        <>
            {isLoadingData && <LoadingAnimation />}
            <div className="page-content">
                <NotificationContainer>
                    {
                        isUpdated && <NotificationModal closeModal={() => setIsUpdated(false)} type="success" title="Успешно обновлен" duration={2000} />
                    }
                    {
                        distributionError && <NotificationModal closeModal={() => setDistributionError(null)} type="error" subTitle={distributionError} title="Ошибка" duration={3000} />
                    }
                </NotificationContainer>
                <div className='page-path'>
                    <span className='page-path-previous' style={{ cursor: "pointer" }} onClick={() => navigate("/distribution")}>
                        Распределение по филиалам
                    </span>
                    <ChevronLeft color='#4A4A4A' />
                    <span className='page-path-current'>
                        {filters?.branch_name}
                    </span>
                </div>

                <div className="page-header-container">
                    <h1 className="page-title">Распределение по филиалу г. {filters?.branch_name}, ({filters?.hub_name}) {planningDate && "на " + formatToAbbrevMonthYear(planningDate, true)}</h1>
                    <div className='page-header-buttons'>
                        <button type="button" className="primary-button" onClick={handleDownloadExcel}>
                            <DownloadIcon size={24} />
                            {
                                downloading
                                    ? <LoadingIcon size={20} color="#ffffff" className={"loading-icon spinning"} />
                                    : "Скачать Excel"
                            }
                        </button>

                    </div>
                </div>

                <div className='page-subheading-row'>
                    <div className='distribution-info-text'>
                        Расчет по СКЮ
                    </div>
                    <div style={{display: "flex", marginLeft: "auto", gap: 16}}>
                        <div className='distribution-info-card'>
                            {branchSummaryInfo?.total_adjusted_amount_dsp_per_branch && (
                                <InfoTooltip>{branchSummaryInfo.total_adjusted_amount_dsp_per_branch}</InfoTooltip>
                            )}
                            <span className='distribution-info-card-label'>
                                Сумма распределения:
                            </span>
                            <span className='distribution-info-card-value'>
                                {numberToKiloFormat(branchDistributionSummary?.total_adjusted_amount_dsp_per_branch)}
                            </span>
                        </div>

                        <div className='distribution-info-card'>
                            {branchSummaryInfo?.total_adjusted_volume_cbm_per_branch_text && (
                                <InfoTooltip>{branchSummaryInfo.total_adjusted_volume_cbm_per_branch_text}</InfoTooltip>
                            )}
                            <span className='distribution-info-card-label'>
                                Объем распределения:
                            </span>
                            <span className='distribution-info-card-value'>
                                {numberToKiloFormat(branchDistributionSummary?.total_adjusted_volume_cbm_per_branch)}
                            </span>
                        </div>

                        <div className='distribution-info-card'>
                            {branchSummaryInfo?.total_adjusted_gross_weight_kg_per_branch_text && (
                                <InfoTooltip>{branchSummaryInfo.total_adjusted_gross_weight_kg_per_branch_text}</InfoTooltip>
                            )}
                            <span className='distribution-info-card-label'>
                                Общий вес:
                            </span>
                            <span className='distribution-info-card-value'>
                                {numberToKiloFormat(branchDistributionSummary?.total_adjusted_gross_weight_kg_per_branch)}
                            </span>
                        </div>

                    </div>
                </div>

                <div className="data-table-container">
                    <table className="data-table branch-distribution-table">
                        <thead>
                            <tr>
                                {COLUMNS.map(({ key, label }) => {
                                    const hasFilter = key in filterOptions;
                                    return (
                                        <th key={key}>
                                            <div style={{ display: 'flex', height: '100%', marginBottom: 'auto', gap: 6,}}>
                                                
                                                <span>{label}</span>
                                                {hasFilter && (
                                                    <ColumnFilterDropdown
                                                        label={label}
                                                        options={colOptions[key] || []}
                                                        selected={getColSelected(key)}
                                                        onApply={(vals) => handleColFilterApply(key, vals)}
                                                    />
                                                )}
                                                {
                                                    key === "readiness_for_target_per_sku" && readinessInfo?.readiness_for_target_per_sku_text &&
                                                        <InfoTooltip>
                                                            {readinessInfo.readiness_for_target_per_sku_text}
                                                        </InfoTooltip>
                                                }
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                (distributionBranchData && distributionBranchData.length > 0) ? distributionBranchData.map((row, mainIndex) => {
                                    const key = distributionBranchCellKey(row, mainIndex);
                                    const isBranchEdit = editingDistributionBranchRows[key] !== undefined && editingDistributionBranchRows[key] !== false;

                                    return (
                                        <tr key={mainIndex}>
                                            <td>
                                                {row.sku_code}
                                            </td>
                                            <td>
                                                {row.sku_name}
                                            </td>
                                            <td>
                                                {row.total_available_quantity_in_mc?.toLocaleString("ru-RU")}
                                            </td>
                                            <td>
                                                {row.available_quantity_in_mc?.toLocaleString("ru-RU")}
                                            </td>
                                            <td>
                                                {row.average_l3m_quantity_in_mc?.toLocaleString("ru-RU")}
                                            </td>
                                            <td>
                                                {row.average_f3m_quantity_in_mc?.toLocaleString("ru-RU")}
                                            </td>
                                            <td>
                                                {row.recommended_quantity_in_mc?.toLocaleString("ru-RU")}
                                            </td>
                                            <td style={{minWidth: "160px"}}>
                                                {/* {row.adjusted_quantity_in_mc?.toLocaleString("ru-RU")} */}
                                                <EditableCell
                                                    value={row.adjusted_quantity_in_mc}
                                                    isEditing={isBranchEdit}
                                                    onChange={(e) => setDistributionBranchData(prev =>
                                                        prev.map((item, index) =>
                                                            index === mainIndex
                                                                ? { ...item, adjusted_quantity_in_mc: e.target.value }
                                                                : item
                                                        )
                                                    )}
                                                    onEdit={() => setEditingDistributionBranchRows(prev => ({ ...prev, [key]: row.adjusted_quantity_in_mc }))}
                                                    onConfirm={() => {
                                                        handleUpdateAdjustedQty(row.sku_code, row.adjusted_quantity_in_mc);
                                                        setEditingDistributionBranchRows(prev => ({ ...prev, [key]: false }));
                                                    }}
                                                    onCancel={() => {
                                                        setDistributionBranchData(prev => prev.map((item, i) =>
                                                            i === mainIndex ? { ...item, adjusted_quantity_in_mc: editingDistributionBranchRows[key] } : item
                                                        ));
                                                        setEditingDistributionBranchRows(prev => ({ ...prev, [key]: false }));
                                                    }}
                                                    renderValue={(v) => v?.toLocaleString("ru-RU")}
                                                    editingContainerStyle={{width: "auto"}}
                                                    displayContainerStyle={{maxWidth: "140px"}}
                                                />
                                            </td>
                                            {/* <td>
                                                <BatteryCell value={row.readiness_for_target_per_sku} />
                                            </td> */}
                                        </tr>
                                    )
                                }) : <tr key={"distribution-list-1"} >
                                    <td style={{ margin: "auto", textAlign: 'center' }} colSpan="8">Нет данных</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                    <Pagination
                        currentPage={filters.page}
                        totalItems={distributionBranchTotalItems}
                        onPageChange={(p) => updateFilters({ page: p })}
                        rowsPerPage={filters.page_size}
                        onRowsPerPageChange={(r) => { updateFilters({ page_size: r }) }}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </div>
            </div>
        </>
    );
}

